import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { ApiResponse } from '../../../types/api';

interface ChecklistItem {
  id: number;
  descricao: string;
  tipo: string;
  obrigatorio: boolean;
  opcoes?: string[];
  ordem: number;
  observacao?: string;
}

interface ChecklistGroup {
  id: number;
  nome: string;
  descricao?: string;
  ordem: number;
  itens: ChecklistItem[];
}

interface ChecklistPersonalizado {
  id?: number;
  veiculo_id: number;
  ativo: boolean;
  grupos: ChecklistGroup[];
  agendamento_automatico: {
    ativo: boolean;
    dias_semana: string[];
    horarios: string[];
  };
  whatsapp_config: {
    numero_padrao: string;
    conexao_ativa: boolean;
  };
}

const VehicleChecklistPersonalizadoView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [checklist, setChecklist] = useState<ChecklistPersonalizado | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChecklist = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Verificar se o ID é válido (não NaN ou undefined)
      const vehicleId = Number(id);
      if (isNaN(vehicleId) || !id) {
        // Para criação de veículo, usar estado vazio
        setChecklist({
          veiculo_id: 0, // ID temporário para criação
          ativo: false,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false
          }
        });
        setIsLoading(false);
        return;
      }
      
      const response = await apiService.getVeiculoChecklistAdaptadoByVehicle(vehicleId) as ApiResponse<any>;
      
      if (response.success && response.data) {
        setChecklist(response.data);
      } else {
        // Se não existe checklist, criar um padrão vazio
        setChecklist({
          veiculo_id: vehicleId,
          ativo: false,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false
          }
        });
      }
    } catch (error) {
      console.error('Erro ao carregar checklist:', error);
      setError('Erro ao carregar checklist personalizado');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getTipoItemText = (tipo: string): string => {
    const tipoMap: Record<string, string> = {
      'texto': 'Texto',
      'sim_nao': 'Sim/Não',
      'opcoes': 'Opções',
      'numerico': 'Numérico',
      'data': 'Data'
    };
    return tipoMap[tipo] || tipo;
  };

  const getObrigatorioText = (obrigatorio: boolean): string => {
    return obrigatorio ? 'Sim' : 'Não';
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#04C64F', marginBottom: '20px' }}></i>
          <p>Carregando checklist personalizado...</p>
        </div>
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#e74c3c', marginBottom: '20px' }}></i>
          <p>Erro ao carregar checklist personalizado.</p>
          <button className="btn btn-primary" onClick={loadChecklist}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Status do Checklist */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            <i className="fas fa-clipboard-check"></i>
            Checklist Personalizado
          </h3>
        </div>
        <div className="card-body">
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="info-item">
              <label className="form-label">Status do Checklist</label>
              <span className="info-value">
                <span className={`status-badge ${checklist.ativo ? 'status-active' : 'status-inactive'}`}>
                  {checklist.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </span>
            </div>

            <div className="info-item">
              <label className="form-label">Total de Grupos</label>
              <span className="info-value">{checklist.grupos.length}</span>
            </div>

            <div className="info-item">
              <label className="form-label">Total de Itens</label>
              <span className="info-value">
                {checklist.grupos.reduce((total, grupo) => total + grupo.itens.length, 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Grupos e Itens */}
      {checklist.grupos.length > 0 ? (
        <div style={{ marginTop: '20px' }}>
          {checklist.grupos.map((grupo, grupoIndex) => (
            <div key={grupo.id} className="card" style={{ marginBottom: '20px' }}>
              <div className="card-header">
                <h4 style={{ margin: 0, color: '#2c3e50' }}>
                  <i className="fas fa-folder"></i>
                  {grupo.nome}
                  {grupo.descricao && (
                    <small style={{ marginLeft: '10px', color: '#7f8c8d', fontWeight: 'normal' }}>
                      {grupo.descricao}
                    </small>
                  )}
                </h4>
                <span className="badge badge-secondary" style={{ marginLeft: '10px' }}>
                  Ordem: {grupo.ordem}
                </span>
              </div>
              <div className="card-body">
                {grupo.itens.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: '5%' }}>#</th>
                          <th style={{ width: '40%' }}>Descrição</th>
                          <th style={{ width: '15%' }}>Tipo</th>
                          <th style={{ width: '10%' }}>Obrigatório</th>
                          <th style={{ width: '20%' }}>Opções</th>
                          <th style={{ width: '10%' }}>Ordem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.itens.map((item, itemIndex) => (
                          <tr key={item.id}>
                            <td>{itemIndex + 1}</td>
                            <td>
                              <div>
                                <strong>{item.descricao}</strong>
                                {item.observacao && (
                                  <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: '5px' }}>
                                    {item.observacao}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="badge badge-info">
                                {getTipoItemText(item.tipo)}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${item.obrigatorio ? 'badge-warning' : 'badge-secondary'}`}>
                                {getObrigatorioText(item.obrigatorio)}
                              </span>
                            </td>
                            <td>
                              {item.opcoes && item.opcoes.length > 0 ? (
                                <div style={{ fontSize: '0.85rem' }}>
                                  {item.opcoes.join(', ')}
                                </div>
                              ) : (
                                <span style={{ color: '#95a5a6' }}>-</span>
                              )}
                            </td>
                            <td>
                              <span className="badge badge-light">{item.ordem}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#95a5a6' }}>
                    <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                    <p>Nenhum item cadastrado neste grupo.</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
          <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
            <i className="fas fa-clipboard-list" style={{ fontSize: '3rem', color: '#95a5a6', marginBottom: '15px' }}></i>
            <h4 style={{ color: '#7f8c8d', marginBottom: '10px' }}>Nenhum Checklist Configurado</h4>
            <p style={{ color: '#95a5a6' }}>
              Este veículo ainda não possui um checklist personalizado configurado.
              Para configurar, edite o veículo e acesse a aba "Checklist Personalizado".
            </p>
          </div>
        </div>
      )}

      {/* Resumo do Checklist */}
      {checklist.grupos.length > 0 && (
        <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
          <div className="card-header">
            <h4 style={{ margin: 0, color: '#2c3e50' }}>
              <i className="fas fa-chart-bar"></i>
              Resumo do Checklist
            </h4>
          </div>
          <div className="card-body">
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Status</label>
                <span className="info-value">
                  <span className={`status-badge ${checklist.ativo ? 'status-active' : 'status-inactive'}`}>
                    {checklist.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Total de Grupos</label>
                <span className="info-value">{checklist.grupos.length}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Total de Itens</label>
                <span className="info-value">
                  {checklist.grupos.reduce((total, grupo) => total + grupo.itens.length, 0)}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Itens Obrigatórios</label>
                <span className="info-value">
                  {checklist.grupos.reduce((total, grupo) => 
                    total + grupo.itens.filter(item => item.obrigatorio).length, 0
                  )}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Itens com Opções</label>
                <span className="info-value">
                  {checklist.grupos.reduce((total, grupo) => 
                    total + grupo.itens.filter(item => item.opcoes && item.opcoes.length > 0).length, 0
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleChecklistPersonalizadoView;
